import React, { useState } from "react";
import { Box, CircularProgress, Button, Alert } from "@mui/material";

import theme, { colors, RightContainerStyled } from "../../assets/styles/theme";
import Typography from "@mui/material/Typography";

import { CredentialConfigWrapper } from "./styles/CredentialConfigComponents";
import { useQueryClient } from "@tanstack/react-query";
import { DataSourceAccordion } from "./DataSourceAccordion";

import {
  useGetDataSourcesConnectionsCred,
  useDeleteDataSourceConnection,
} from "../hooks";

import DeleteFile from "./modals/DeleteFile";
import SnackbarComponent from "./shared/SnackBarComponent";
import SnackbarSuccessComponent from "./shared/SnackBarSuccessComponent";

const CredentialConfigTestComponents: React.FC = () => {
  const [expandedAccordion, setExpandedAccordion] = useState(null);
  const [globalEditing, setGlobalEditing] = useState(false);

  const queryClient = useQueryClient();
  const [error, setError] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const [success, setSuccess] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState("");

  const [openDeleteModal, setOpenDeleteModal] = React.useState(false);
  const [selectedConnections, setSelectedConnections] = React.useState<any>({});

  const {
    data: getDataSourcesConnectionsCredData,
    isLoading: isGetDataSourcesConnectionsCredDataPending,
    error: getDataSourcesConnectionsCredError,
  } = useGetDataSourcesConnectionsCred();

 
  const {
    mutate: deleteDataSourceConnection,
    isPending: isDataSourceConnectionPending,
  } = useDeleteDataSourceConnection();


  // Handle accordion expansion
  const handleAccordionExpand11 = (accordionId: any) => {
    setExpandedAccordion(
      expandedAccordion === accordionId ? null : accordionId
    );
  };

  const handleDelete = (item: any, onDeleteCallback: (success: boolean, error?: any) => void) => {
    console.log("handleDelete", item);
    setOpenDeleteModal(true);
    setSelectedConnections(item);
  
    // Store the callback function in the state or a ref so it can be used later
    setSelectedConnections((prev: any) => ({
      ...prev,
      onDeleteCallback, // Store the callback
    }));
  };

  const handleDeleteFileCancel = () => {
    setOpenDeleteModal(false);
  };

  const handleDeleteFileConfirm = () => {
    deleteDataSourceConnection(selectedConnections.dataSourceConnectionId, {
      onSuccess: (response: any) => {
        onDeleteDataSourceConnectionSuccess(response);
  
        // Invoke the callback with success status
        if (selectedConnections.onDeleteCallback) {
          selectedConnections.onDeleteCallback(true);
        }
      },
      onError: (error: any) => {
        onDeleteDataSourceConnectionError(error);
  
        // Invoke the callback with error status
        if (selectedConnections.onDeleteCallback) {
          selectedConnections.onDeleteCallback(false, error);
        }
      },
    });
  };

  const onDeleteDataSourceConnectionSuccess = (response: any) => {
    console.log("success", response);
    const deletedSuccessMessage = `${selectedConnections.connectionName} has been successfully deleted.`;
    setOpenDeleteModal(false);
    setSuccess(true);
    setSuccessMessage(deletedSuccessMessage);
    setSelectedConnections({});
  };

  const onDeleteDataSourceConnectionError = (error: any) => {
    console.log("error", error);
    setOpenDeleteModal(false);
    setSelectedConnections({});
    setError(true);
    setErrorMessage(error?.data?.message || error?.message);
  };

  const hideError = () => {
    setError(false);
    setErrorMessage("");
  };

  const hideSuccess = () => {
    setSuccess(false);
    setSuccessMessage("");
  };

  const updateSuccess = (name : any) => {
    setGlobalEditing(false)
    setSuccess(true);
    setSuccessMessage(name);
  };

  const updateCancel = () => {
    setGlobalEditing(false)
  };

  const updateEdit = () => {
    setGlobalEditing(true)
  };

  const updateError = (err : any) => {
    setGlobalEditing(false)
    setError(true);
    setErrorMessage(err);
  };

  const handleAccordionExpand = (dataSourceId: any) => {
    setExpandedAccordion(dataSourceId);
  
    const performScroll = () => {
      try {
        const accordionElement = document.querySelector(`[data-datasource-id="${dataSourceId}"]`);
        
        if (accordionElement) {
          accordionElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
  
          const elementTop = accordionElement.getBoundingClientRect().top + window.pageYOffset;
          
          window.scrollTo({
            top: Math.max(0, elementTop - 100),
            behavior: 'smooth'
          });
        }
      } catch (error) {
        console.error('Scroll to accordion failed:', error);
      }
    };
  
    requestAnimationFrame(() => {
      setTimeout(performScroll, 100);
    });
  };
  

  if (isGetDataSourcesConnectionsCredDataPending) {
    return (
      <Box className="centerAlignLoader">
        <CircularProgress size="1.5rem" sx={{ mt: 1.5, mb: 1 }} />
      </Box>
    );
  }
  

  if (getDataSourcesConnectionsCredError) {
    return (
      <Box>
        <Alert severity="error" sx={{ m: 3 }}>
          Error loading data sources: {getDataSourcesConnectionsCredError.message}
        </Alert>
      </Box>
    );
  }

  return (
    <RightContainerStyled>     
      <Typography variant="h1" component="h1" className="">
        CREDENTIAL CONFIGURATIONS
      </Typography>

      <CredentialConfigWrapper>
        {!isGetDataSourcesConnectionsCredDataPending && getDataSourcesConnectionsCredData &&
          getDataSourcesConnectionsCredData?.data?.map((dataSource: any) => (
            <DataSourceAccordion
              key={dataSource.dataSourceId}
              dataSource={dataSource}
              data-datasource-id={dataSource.dataSourceId}
              expanded={expandedAccordion === dataSource.dataSourceId}
              onExpand={() => handleAccordionExpand(dataSource.dataSourceId)}
              onDelete={handleDelete}
              updateError={updateError}
              updateSuccess={updateSuccess}
              globalEditing={globalEditing}
              updateCancel={updateCancel}
              updateEdit={updateEdit}
              isGetDataSourcesConnectionsCredDataPending={isGetDataSourcesConnectionsCredDataPending}
            />
          ))}
        {openDeleteModal && (
          <DeleteFile
            open={openDeleteModal}
            handleCancel={handleDeleteFileCancel}
            handleConfirm={handleDeleteFileConfirm}
            subject={"Delete"}
            status={null}
            fileName={selectedConnections?.connectionName}
            isLoading={isDataSourceConnectionPending}
          />
        )}
        {error && (
          <SnackbarComponent
            error={error}
            errorMessage={errorMessage}
            hideError={hideError}
          />
        )}
        {success && (
          <SnackbarSuccessComponent
            success={success}
            successMessage={successMessage}
            hideSuccess={hideSuccess}
          />
        )}
      </CredentialConfigWrapper>
    </RightContainerStyled>
  );
};

export default CredentialConfigTestComponents;
